import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TheLastSpellingBeeReCaptcha } from "the-last-spelling-bee-re-captcha";
import { useAppDispatch } from "../../redux/hooks/useTypedSelector";
import { contactUsAction } from "../../redux/slice/contact-us/contactUsSlice";
import {
  ContactUs as ContactUsProps,
  contactUsValidationSchema,
} from "../auth/schema";
import { theLastSpellingBeeReCaptchaKey } from "../../config";
import { PageHero } from "../../components/public/PageHero";
import { SectionLink } from "../../components/public/SectionLink";
import { Icon } from "../../components/public/ui";
import {
  Field,
  FormAlert,
  inputClass,
  SubmitButton,
} from "../../components/public/auth/fields";
import { authErrorMessage } from "../../components/public/auth/authErrorMessage";

const subjects = [
  { value: "Report a bug", label: "Report a bug" },
  { value: "Partnership", label: "Partnership" },
  { value: "Others", label: "Something else" },
];

// Public "Contact Us" page (the dashboard keeps using GlobalContactUs)
export const PublicContactUs = () => {
  const dispatch = useAppDispatch();
  const [captchaPassed, setCaptchaPassed] = useState(false);
  // Changing the key remounts the captcha so each message needs a fresh check
  const [captchaKey, setCaptchaKey] = useState(0);
  const [status, setStatus] = useState<{
    tone: "success" | "error";
    text: string;
  } | null>(null);

  const {
    handleSubmit,
    register,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: yupResolver(contactUsValidationSchema),
    defaultValues: { subject: "", fullName: "", email: "", message: "" },
  });

  const onSubmit = async (data: ContactUsProps) => {
    setStatus(null);
    const res = await dispatch(contactUsAction(data));
    if (contactUsAction.fulfilled.match(res)) {
      setStatus({
        tone: "success",
        text: "Thanks! Your message has been sent. We'll get back to you by email.",
      });
      reset();
      setCaptchaPassed(false);
      setCaptchaKey((k) => k + 1);
    } else {
      setStatus({
        tone: "error",
        text: authErrorMessage(
          res.payload,
          "We couldn't send your message. Please try again.",
        ),
      });
    }
  };

  return (
    <main>
      <PageHero
        eyebrow="Get in Touch"
        title="Contact Us"
        description="Found a bug, interested in partnering, or have a question about your preparation? Send us a message."
      />

      <section className="w-full pb-20 bg-surface-container-low/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8 items-start">
          {/* Contact details */}
          <div className="lg:col-span-2 flex flex-col gap-4 sm:gap-6 order-2 lg:order-1">
            <div className="bg-surface-container-lowest rounded-2xl p-6 shadow-sm flex items-start gap-4">
              <div className="w-11 h-11 shrink-0 rounded-xl bg-tertiary-fixed text-tertiary flex items-center justify-center">
                <Icon name="help" className="text-[22px]" />
              </div>
              <div className="flex flex-col gap-1">
                <h2 className="font-headline-sm text-headline-sm text-on-surface">
                  Quick answers
                </h2>
                <p className="font-body-sm text-body-sm text-on-surface-variant">
                  Many common questions about plans, mock tests and devices are
                  answered in our FAQs.
                </p>
                <SectionLink
                  section="faqs"
                  className="inline-flex items-center gap-1.5 font-label-md text-label-md text-tertiary hover:gap-2.5 transition-all"
                >
                  Read the FAQs
                  <Icon name="arrow_forward" className="text-base" />
                </SectionLink>
              </div>
            </div>
            <div className="bg-surface-container-lowest rounded-2xl p-6 shadow-sm flex items-start gap-4">
              <div className="w-11 h-11 shrink-0 rounded-xl bg-secondary-fixed text-secondary flex items-center justify-center">
                <Icon name="handshake" className="text-[22px]" />
              </div>
              <div className="flex flex-col gap-1">
                <h2 className="font-headline-sm text-headline-sm text-on-surface">
                  Partnerships
                </h2>
                <p className="font-body-sm text-body-sm text-on-surface-variant">
                  Teachers, schools and immigration consultants — choose
                  "Partnership" as the subject and tell us about your needs.
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3 bg-surface-container-lowest rounded-3xl shadow-xl p-6 sm:p-8 order-1 lg:order-2">
            <form
              className="flex flex-col gap-5"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              <div className="flex flex-col gap-1">
                <h2 className="font-headline-md text-headline-md text-on-surface">
                  Send a message
                </h2>
                <p className="font-body-sm text-body-sm text-on-surface-variant">
                  All fields are required.
                </p>
              </div>

              {status && (
                <FormAlert tone={status.tone}>{status.text}</FormAlert>
              )}

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="contact-subject"
                  className="font-label-md text-label-md text-on-surface"
                >
                  Subject
                </label>
                <div className="relative">
                  <select
                    id="contact-subject"
                    className={`${inputClass} appearance-none pr-12 cursor-pointer ${errors.subject ? "border-error" : ""}`}
                    aria-invalid={!!errors.subject}
                    {...register("subject")}
                  >
                    <option value="">Select a subject</option>
                    {subjects.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <Icon
                    name="expand_more"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none"
                  />
                </div>
                {errors.subject && (
                  <span className="font-body-sm text-xs text-error">
                    {errors.subject.message}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field
                  id="contact-name"
                  label="Full name"
                  autoComplete="name"
                  placeholder="Your full name"
                  error={errors.fullName?.message}
                  {...register("fullName")}
                />
                <Field
                  id="contact-email"
                  label="Email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  error={errors.email?.message}
                  {...register("email")}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="contact-message"
                  className="font-label-md text-label-md text-on-surface"
                >
                  Message
                </label>
                <textarea
                  id="contact-message"
                  rows={6}
                  placeholder="How can we help?"
                  aria-invalid={!!errors.message}
                  className={`${inputClass} h-auto py-3 resize-y min-h-[9rem] ${errors.message ? "border-error" : ""}`}
                  {...register("message")}
                />
                {errors.message && (
                  <span className="font-body-sm text-xs text-error">
                    {errors.message.message}
                  </span>
                )}
              </div>

              <div className="rounded-2xl bg-surface-container-low p-4 flex flex-col gap-3 overflow-x-auto">
                <span className="font-label-md text-label-md text-on-surface flex items-center gap-2">
                  <Icon name="verified_user" className="text-primary text-lg" />
                  Quick check: are you human?
                </span>
                <TheLastSpellingBeeReCaptcha
                  key={captchaKey}
                  reCaptchaKey={theLastSpellingBeeReCaptchaKey}
                  questionType="RANDOM"
                  onVerifyCaptcha={(verified: boolean) =>
                    setCaptchaPassed(verified)
                  }
                />
              </div>

              {captchaPassed ? (
                <SubmitButton loading={isSubmitting}>Send Message</SubmitButton>
              ) : (
                <p className="font-body-sm text-xs text-on-surface-variant text-center">
                  Complete the check above to send your message.
                </p>
              )}
            </form>
          </div>
        </div>
      </section>
    </main>
  );
};
